/**
 * Tile API service — manages raster tile generation for large image layers.
 */

import { API_BASE, apiFetch } from './apiFetch';

// ─── Types ───────────────────────────────────────────────────────────

export interface TilesetInfo {
  id: string;
  siteId: string;
  layerType: 'orthophoto' | 'overlay';
  layerId: string;
  status: 'pending' | 'generating' | 'ready' | 'error';
  progress: number;
  error?: string;
  minZoom?: number;
  maxZoom?: number;
  tileCount: number;
  bounds?: { minLng: number; minLat: number; maxLng: number; maxLat: number };
  width: number;
  height: number;
  bgRemoved: boolean;
  /** P2-A: R2 key of the Cloud-Optimized GeoTIFF when this tileset is served
   *  on-demand from a COG (null ⇒ legacy pre-generated tiles). */
  cogR2Key?: string | null;
  /** P2-A ingest status: null | 'pending' | 'ready' | 'error'. */
  cogStatus?: string | null;
  /** Regen lineage: the tileset this one replaces. Present while a regen is in
   *  flight or after it failed (on success the server deletes the prior). The
   *  poller rolls back to it when a regen errors. */
  priorTilesetId?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateTilesetResult {
  tilesetId: string;
  status: string;
  callbackUrl: string;
}

/** Bake parameters sent with a PDF plan source (see PlanBake in projectStore).
 *  The server takes only what it is sent and clamps all of it, so a pixel-
 *  affecting field missing here is silently ignored at bake time. */
export interface PlanBakeRequest {
  pageIndex: number;
  pageWidthPt: number;
  pageHeightPt: number;
  dpi: number;
  whiteThreshold: number;
  /** Crop window in page fractions; absent = the whole page. */
  cropRect?: { u0: number; v0: number; u1: number; v1: number };
  /** Erased areas as polygons of [u,v] page fractions. */
  erasePolygons?: Array<Array<[number, number]>>;
}

/** Default render resolution for a plan bake. The server lowers it when the
 *  page is large enough that this would exceed its pixel ceilings — a 35-inch
 *  CAD sheet resolves to ~21k × 20k px, i.e. a couple of cm on the ground. */
export const PLAN_BAKE_DEFAULT_DPI = 600;

// ─── API Calls ───────────────────────────────────────────────────────

export async function requestTileGeneration(
  siteId: string,
  layerType: 'orthophoto' | 'overlay',
  layerId: string,
  sourceR2Key: string,
  bounds: { minLng: number; minLat: number; maxLng: number; maxLat: number },
  corners: number[][],
  width: number,
  height: number,
  bgRemove: boolean,
  /** Regen: the previous tilesetId — the server prefers its COG as the source
   * (survives raw retention deletes; lossless retag fast path). */
  priorTilesetId?: string,
  /** Plan bake (PDF sources only): render parameters for the converter. The
   * server validates and clamps them, and derives width/height from the page
   * geometry — the width/height passed above are ignored for a PDF source. */
  bake?: PlanBakeRequest
): Promise<CreateTilesetResult> {
  return apiFetch<CreateTilesetResult>(
    `/api/v2/sites/${siteId}/tilesets`,
    {
      method: 'POST',
      body: JSON.stringify({
        layerType,
        layerId,
        sourceR2Key,
        bounds,
        corners,
        width,
        height,
        bgRemove,
        priorTilesetId,
        bake,
      }),
    },
    // NOTE: handleCreateTileset no longer blocks on the tiler/converter — it
    // dispatches FIRE-AND-FORGET (executionCtx.waitUntil) and returns
    // 'generating' + the tilesetId immediately, then the poller + watchdog track
    // completion. So this request itself is fast; the generous timeout is just
    // headroom for slow networks, not a wait on the (multi-minute) tile/COG job.
    6 * 60 * 1000,
  );
}

export async function getTilesetStatus(
  siteId: string,
  tilesetId: string
): Promise<TilesetInfo> {
  return apiFetch<TilesetInfo>(
    `/api/v2/sites/${siteId}/tilesets/${tilesetId}`,
  );
}

export async function listTilesets(
  siteId: string
): Promise<TilesetInfo[]> {
  return apiFetch<TilesetInfo[]>(
    `/api/v2/sites/${siteId}/tilesets`,
  );
}

export async function deleteTileset(
  siteId: string,
  tilesetId: string
): Promise<void> {
  await apiFetch<unknown>(
    `/api/v2/sites/${siteId}/tilesets/${tilesetId}`,
    { method: 'DELETE' }
  );
}

// ─── Server-side Preview ────────────────────────────────────────────

export interface PreviewResult {
  previewR2Key: string;
  previewUrl: string;
  width: number;
  height: number;
  elapsed: number;
}

/**
 * Request a server-side downsampled preview for an image too large for browser decode.
 * The Lambda generates a ~2000px PNG and uploads it to R2.
 */
export async function requestPreview(
  siteId: string,
  layerId: string,
  sourceR2Key: string,
  bgRemove: boolean
): Promise<PreviewResult> {
  return apiFetch<PreviewResult>(
    `/api/v2/sites/${siteId}/preview`,
    {
      method: 'POST',
      body: JSON.stringify({ layerId, sourceR2Key, bgRemove }),
    }
  );
}

export function buildTileUrl(tilesetId: string): string {
  return `${API_BASE}/api/v2/tiles/${tilesetId}/{z}/{x}/{y}.png`;
}

/**
 * Bake a PDF plan overlay to full-resolution tiles.
 *
 * NON-DESTRUCTIVE, exactly like an orthophoto regen: passing the current
 * tilesetId as `priorTilesetId` keeps the existing tiles serving until the new
 * bake is verified, and the server deletes the old generation itself. On
 * failure the poller rolls the overlay back to the tileset it was already
 * showing.
 *
 * Unlike an ortho regen, the source is ALWAYS the PDF — a plan bake is placed
 * by warping, so its COG is deliberately not re-sourceable, and re-rendering
 * from the PDF is what lets changed bake parameters take effect.
 */
export async function requestPlanBake(
  siteId: string,
  overlay: {
    id: string;
    bounds: number[][];
    sourceR2Key?: string;
    tilesetId?: string;
  },
  bake: PlanBakeRequest
): Promise<CreateTilesetResult> {
  if (!overlay.sourceR2Key) {
    throw new Error('This plan has no PDF stored on the server yet — save the project first.');
  }
  const corners = overlay.bounds.map((c) => [c[0], c[1]]);
  const lngs = corners.map((c) => c[0]);
  const lats = corners.map((c) => c[1]);
  const bounds = {
    minLng: Math.min(...lngs),
    minLat: Math.min(...lats),
    maxLng: Math.max(...lngs),
    maxLat: Math.max(...lats),
  };
  return requestTileGeneration(
    siteId,
    'overlay',
    overlay.id,
    overlay.sourceR2Key,
    bounds,
    corners,
    0, // width/height are server-derived from the page geometry for PDF sources
    0,
    false,
    overlay.tilesetId,
    bake
  );
}

/**
 * Regenerate tiles for an orthophoto. NON-DESTRUCTIVE: the old tileset is NOT
 * deleted here — the server sources the new conversion from the old tileset's
 * COG (via priorTilesetId), so the old one must stay alive until the new one
 * completes. The SERVER owns the deferred cleanup (it deletes the prior only
 * on a verified cog_ready — durable across reloads); on error the tile-status
 * poller rolls the ortho back to the prior tileset, which the error path never
 * deletes.
 */
export async function regenerateTiles(
  siteId: string,
  ortho: {
    id: string;
    tilesetId?: string;
    sourceR2Key?: string;
    bounds: { minLng: number; minLat: number; maxLng: number; maxLat: number };
    coordinates: number[][];
    width?: number;
    height?: number;
    bgRemoved?: boolean;
  }
): Promise<CreateTilesetResult> {
  // A prior tileset (→ its COG) OR a raw source can feed the regen.
  if (!ortho.sourceR2Key && !ortho.tilesetId) {
    throw new Error('No source available for regeneration');
  }
  if (!ortho.width || !ortho.height) throw new Error('Missing image dimensions');

  const corners = ortho.coordinates.map((c) => [c[0], c[1]]);
  return requestTileGeneration(
    siteId,
    'orthophoto',
    ortho.id,
    ortho.sourceR2Key ?? '', // raw fallback; the server prefers the prior COG
    ortho.bounds,
    corners,
    ortho.width,
    ortho.height,
    ortho.bgRemoved ?? false,
    ortho.tilesetId
  );
}

// ─── Vector-plan DXF ingest (rung 3, R3-A) ───────────────────────────

export interface VectorIngestResult {
  jobId: string;
  /** R2 prefix of the ingest outputs — sites/<siteId>/dxfingest/<jobId>/.
   *  Everything under it (manifest.json, layers/<n>.json, texts.json,
   *  done.json) is served by the ordinary asset routes; completion is polled
   *  by fetching done.json until it exists. */
  outPrefix: string;
}

/**
 * Dispatch a server-side parse of an uploaded .dxf into per-layer GeoJSON +
 * text entities. The request returns as soon as the container accepts the job
 * (the parse itself is seconds — CPU-light next to a COG conversion); the
 * caller stamps overlay.dxf = { status: 'ingesting' } and useDxfIngestPoller
 * watches for done.json.
 */
export async function requestVectorIngest(
  siteId: string,
  srcKey: string,
  /** Plan overlay this parse belongs to. Optional and advisory — it is recorded
   *  on the server's job row so a job can be traced back to its plan when the
   *  client loses the response. */
  overlayId?: string,
  /** For a .pdf srcKey (R3-D): the 0-based page to vectorize — the plan's
   *  baked page, so the linework matches the raster underneath. Ignored for
   *  a .dxf. */
  pageIndex?: number,
): Promise<VectorIngestResult> {
  return apiFetch<VectorIngestResult>(
    `/api/v2/sites/${siteId}/vector-ingest`,
    { method: 'POST', body: JSON.stringify({ srcKey, overlayId, pageIndex }) },
    60 * 1000,
  );
}
